<template>
  <section id="batch-country-table">
    <b-table
      :data="items"
      :mobile-cards="false"
      narrowed
      sticky-header
      :row-class="rowClass"
      :height="height"
    >
      <b-table-column label="" width="40" centered v-slot="props">
        <b-tooltip v-show="props.row.error" position="is-right">
          <b-icon icon="information" type="is-danger"></b-icon>
          <template v-slot:content>
            {{ props.row.error }}
          </template>
        </b-tooltip>
      </b-table-column>

      <b-table-column
        field="code"
        :label="$t('attributes.group_code')"
        width="100"
        centered
        sortable
        v-slot="props"
      >
        <b-tooltip type="is-light" position="is-right">
          <p>{{ props.row.code }}</p>
          <template v-slot:content>
            <p v-html="props.row.name"></p>
          </template>
        </b-tooltip>
      </b-table-column>

      <b-table-column
        field="departure_date"
        :label="$t('attributes.departure_date')"
        width="100"
        sortable
        centered
        v-slot="props"
      >
        {{ formatDate(props.row.departure_date) }}
      </b-table-column>

      <b-table-column
        field="countries"
        :label="$t('attributes.national_flag')"
        v-slot="props"
      >
        <b-taginput
          :ref="`countries-input-${props.row.code}`"
          v-model="props.row.countryList"
          :data="filteredCountries"
          :maxtags="maxCountry"
          :has-counter="false"
          autocomplete
          keep-first
          field="name"
          icon="label"
          :placeholder="
            props.row.countries.length > maxCountry
              ? '最多只可選6個國旗'
              : '選擇國旗'
          "
          @typing="search => filteringCountries(props.row, search)"
          @input="modified(props.row)"
        >
          <template v-slot="options">
            {{ options.option.english_name }} {{ options.option.name }}
          </template>
          <template #selected="options">
            <b-tag
              v-for="(tag, index) in options.tags"
              :key="index"
              :tabstop="false"
              closable
              @close="
                $refs[`countries-input-${props.row.code}`].removeTag(
                  index,
                  $event
                )
              "
            >
              <p
                style="display: flex; justify-content: center; align-items: center;"
              >
                <img
                  :src="countryTemplateFlagImage(tag.flag)"
                  :alt="$t('attributes.flag')"
                  style="width: 16px; margin-right: 6px;"
                />
                {{ tag.name }}
              </p>
            </b-tag>
          </template>
          <template #empty>
            搜尋不到
          </template>
        </b-taginput>
      </b-table-column>

      <b-table-column
        width="50"
        :label="$t('attributes.function')"
        centered
        v-slot="props"
      >
        <div class="buttons is-centered">
          <b-tooltip :label="$t('attributes.restore')">
            <b-button
              type="is-info"
              icon-left="restore"
              outlined
              @click.prevent="restore(props.row)"
            >
            </b-button>
          </b-tooltip>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section" style="height: 100%">
          <div v-if="!loading" class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-loading :active.sync="isLoading" :is-full-page="false"></b-loading>
  </section>
</template>

<script>
import { COUNTRY_TEMPLATES_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'

export default {
  name: 'BatchCountryTable',
  props: {
    groups: {
      Type: Array,
      required: true
    },
    height: Number,
    loading: Boolean
  },
  data() {
    return {
      items: this.extractGroups(this.groups),

      maxCountry: 6,
      loadingCountries: false,
      countries: [],
      filteredCountries: []
    }
  },
  computed: {
    isLoading() {
      return this.loading || this.loadingCountries
    }
  },
  watch: {
    groups(list) {
      this.items = this.extractGroups(list)
    }
  },
  mounted() {
    this.loadCountries()
  },
  methods: {
    async loadCountries() {
      const api = `${COUNTRY_TEMPLATES_API}/list`

      try {
        this.loadingCountries = true
        const res = await this.axios.get(api)
        this.countries = res.data.data.map(c => ({ ...c, code: c.code.trim() }))

        this.items = this.items.map(group => {
          const countryList = group.countries.map(country => {
            return this.countries.find(c => c.code === country)
          })

          return {
            ...group,
            countryList: countryList
          }
        })
      } catch (e) {
        this.countries = []

        notifyService.fetch_error('country', e)
      } finally {
        this.loadingCountries = false
      }
    },
    filteringCountries(group, search) {
      const name = search.trim().toLowerCase()
      this.filteredCountries = this.countries.filter(option => {
        return (
          option.name
            .trim()
            .toLowerCase()
            .includes(name) ||
          option.english_name
            .trim()
            .toLowerCase()
            .includes(name)
        )
      })
    },
    extractGroups(groups) {
      return groups.map(group => {
        const countries = group.countries ? group.countries.split(',') : []
        const countryList = this.countries
          ? countries.map(country => {
              return this.countries.find(c => c.code === country)
            })
          : []

        return {
          code: group.code,
          departure_date: group.departure_date,
          name: group.name,
          countries: countries,
          countryList: countryList,

          editing: false,
          dirty: false,
          error: null,
          origin: {
            ...group,
            countries: countries
          }
        }
      })
    },

    modified(group) {
      group.countries = group.countryList.map(c => c.code)
      group.dirty = group.countries !== group.origin.countries

      this.validate(group)
    },
    restore(group) {
      group.countries = [...group.origin.countries]
      group.countryList = group.countries.map(country => {
        return this.countries.find(c => c.code === country)
      })

      group.dirty = false
      group.error = false
    },

    isDirty() {
      return this.items.filter(x => x.dirty).length > 0
    },
    isEditing() {
      return this.items.filter(x => x.editing).length > 0
    },
    isValidate() {
      for (const group of this.items) {
        if (!this.validate(group)) {
          return false
        }
      }

      return true
    },
    validate(group) {
      group.error = null
      return true
    },
    getData() {
      return this.items
        .filter(group => group.dirty)
        .map(group => ({
          code: group.code,
          countries: group.countries.join(',')
        }))
    },
    rowClass(group) {
      if (group.error) {
        return 'has-background-danger-light'
      } else if (group.dirty) {
        return 'has-background-warning'
      } else {
        return null
      }
    }
  }
}
</script>

<style scoped></style>
